// Design System
import { IconButton, SearchInput } from "@toddle-edu/ds-web";
import { ChevronDownOutlined } from "@toddle-edu/ds-icons";

// External
import classNames from "classnames";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Hooks
import { useToggleState } from "Hooks";

// Components
import SkeletonLoader from "UIComponents/SkeletonLoader";

// Utils
import { getFormattedUserName } from "Utils/GetFormattedUserName";

// Relative - Helpers
import { isQueryLoading, groupStudentsBySchool } from "../../modules/helpers";

// Relative - Components
import Accordion from "../Accordion";
import ListItem from "../ListItem";

// CSS
import classes from "./StudentsList.scss";

const StudentsList = ({
  selectedStudentIds,
  setSelectedStudentIds,
  data,
  networkStatus,
  studentIdToSchoolIdMap,
  schoolIdToSchoolDataMap,
  schoolIds,
  studentIds,
  testId,
}) => {
  const [searchText, setSearchText] = useState("");
  const [isExpanded, toggleExpand] = useToggleState(true);
  const { t } = useTranslation();

  const showLoader = isQueryLoading(data, networkStatus);

  // Memoize toLowerCase to avoid recalculating on every filter iteration
  const lowerSearchText = useMemo(() => _.toLower(searchText), [searchText]);

  const students = useMemo(() => {
    const studentsData = _.get(data, "multiNode", []);
    return _.chain(studentsData)
      .map(student => ({
        ...student,
        name: getFormattedUserName({ user: student }),
        src: student.profileImage,
      }))
      .filter(({ name }) => _.includes(_.toLower(name), lowerSearchText))
      .value();
  }, [data, lowerSearchText]);

  const structureData = useMemo(() => {
    if (_.size(schoolIds) === 1) {
      return students;
    }

    return groupStudentsBySchool(
      students,
      studentIdToSchoolIdMap,
      schoolIdToSchoolDataMap
    );
  }, [students, schoolIdToSchoolDataMap, schoolIds, studentIdToSchoolIdMap]);

  const onCheckboxClick = useCallback(
    ({ checked }) => {
      setSelectedStudentIds(() => {
        if (checked) {
          return new Set(_.map(students, "id"));
        }
        return new Set();
      });
    },
    [setSelectedStudentIds, students]
  );

  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <div className="text-label">
          {t("common:students_after_count", { count: _.size(studentIds) })}
        </div>

        <IconButton
          testId={`${testId}-toggleExpand`}
          icon={<ChevronDownOutlined variant="subtle" />}
          type="plain"
          variant="neutral"
          isCompact
          onClick={toggleExpand}
          className={classNames(classes.expandButton, {
            [classes.expanded]: isExpanded,
          })}
        />
      </div>

      {isExpanded ? (
        <div className={classes.content}>
          <div className={classes.search}>
            <SearchInput
              value={searchText}
              onChange={setSearchText}
              testId={testId}
              focusSearchBySlash
              placeholder={t("transcript:search_for_students")}
            />
          </div>

          {showLoader ? (
            <div className={classes.skeletonList}>
              <div className={classes.listItem}>
                <div className={classes.name}>
                  <SkeletonLoader height="20px" width="120px" />
                </div>
                <SkeletonLoader height="20px" width="20px" />
              </div>
              {_.map(studentIds, id => (
                <div className={classes.listItem} key={id}>
                  <SkeletonLoader height="28px" width="28px" />
                  <div className={classes.name}>
                    <SkeletonLoader height="20px" width="120px" />
                  </div>
                  <SkeletonLoader height="20px" width="20px" />
                </div>
              ))}
            </div>
          ) : (
            <div className={classes.list}>
              <ListItem
                id="selectAll"
                showAvatar={false}
                name={t("common:label_with_count_parenthesis", {
                  label: t("common:allStudents"),
                  count: _.size(students),
                })}
                isChecked={selectedStudentIds.size === _.size(students)}
                isIndeterminate={
                  selectedStudentIds.size > 0 &&
                  selectedStudentIds.size < _.size(students)
                }
                onCheckboxClick={onCheckboxClick}
                testId={`${testId}-selectAll`}
              />
              {_.map(structureData, ({ id, name, src, options, shape }) => {
                return (
                  <Accordion
                    key={id}
                    id={id}
                    name={name}
                    src={src}
                    options={options}
                    shape={shape}
                    selectedStudentIds={selectedStudentIds}
                    setSelectedStudentIds={setSelectedStudentIds}
                    testId={testId}
                  />
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

StudentsList.propTypes = {
  selectedStudentIds: PropTypes.instanceOf(Set).isRequired,
  setSelectedStudentIds: PropTypes.func.isRequired,
  data: PropTypes.shape({
    multiNode: PropTypes.arrayOf(PropTypes.object),
  }),
  networkStatus: PropTypes.number.isRequired,
  studentIdToSchoolIdMap: PropTypes.objectOf(PropTypes.string).isRequired,
  schoolIdToSchoolDataMap: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      logo: PropTypes.string,
    })
  ).isRequired,
  schoolIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  studentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  testId: PropTypes.string,
};

StudentsList.defaultProps = {
  data: null,
  testId: "",
};

export default memo(StudentsList);
